import React from 'react';
import { WidgetTemplate } from '../../types';
import { widgetRegistry } from '../../components/widgets/registry';
import { catalogMap, getTagLabel } from '../../components/widgets/catalog';
import Modal, { ModalHeader, ModalBody } from '../../components/ui/Modal';
import Badge from '../../components/ui/Badge';
import { formatRelativeDate } from './WidgetTableRow';

function getPreviewCaption(type: string): string {
  const def = widgetRegistry.get(type as any);
  if (def?.dataProvider) return 'Illustrative preview — real data loaded from data provider at runtime';

  const entry = catalogMap.get(type as any);
  const tags = entry?.tags ?? [];
  if (tags.includes('input') && tags.includes('output')) return 'Illustrative preview — content added at runtime';
  if (tags.includes('input')) return 'Illustrative preview — users fill in data during workflow tasks';
  return 'Illustrative preview — content configured by the widget owner';
}

interface WidgetPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  template: WidgetTemplate | null;
  onExport?: (id: string) => void;
  exportingId?: string | null;
}

const WidgetPreviewModal: React.FC<WidgetPreviewModalProps> = ({
  isOpen,
  onClose,
  template,
  onExport,
  exportingId,
}) => {
  if (!template) return null;

  const definition = widgetRegistry.get(template.type);
  const catalogEntry = catalogMap.get(template.type);
  const PreviewComponent = definition?.Preview;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="full">
      <ModalHeader>
        <div className="flex items-center gap-3 pr-8">
          <span className="text-lg font-semibold text-neutral-900">{template.name}</span>
        </div>
      </ModalHeader>

      <ModalBody className="!p-0">
        <div className="flex min-h-[420px]">
          {/* Preview illustration */}
          <div className="flex-1 min-w-0 flex flex-col">
            <div className="px-5 pt-4 pb-2 shrink-0">
              <span className="text-[10px] text-neutral-400 uppercase tracking-wide font-medium">
                {getPreviewCaption(template.type)}
              </span>
            </div>
            {/* Inset well — dark recessed area that makes the preview card pop */}
            <div className="flex-1 min-h-0 mx-5 mb-5 rounded-xl bg-[#111113] overflow-hidden flex items-center justify-center p-6">
              {(() => {
                // Size preview to match widget's default grid dimensions
                // Reference: 1200px container, 24 cols, 8px margin, 8px padding
                const REF_WIDTH = 1200;
                const COLS = 24;
                const MARGIN = 8;
                const PADDING = 8;
                const colW = (REF_WIDTH - PADDING * 2 - (COLS - 1) * MARGIN) / COLS;
                const rowH = colW; // square cells as reference
                const ds = definition?.defaultSize;
                const pw = ds ? ds.w * colW + (ds.w - 1) * MARGIN : 512;
                const ph = ds ? ds.h * rowH + (ds.h - 1) * MARGIN : 400;
                return (
                  <div
                    className="rounded-lg overflow-hidden shadow-[0_8px_32px_rgba(0,0,0,0.5)]"
                    style={{ width: Math.min(pw, 800), height: Math.min(ph, 600), maxWidth: '100%', maxHeight: '100%' }}
                  >
                    {PreviewComponent ? (
                      <PreviewComponent />
                    ) : (
                      <div className="h-full flex flex-col items-center justify-center text-neutral-400 bg-neutral-50">
                        <p className="text-sm">Preview not available</p>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          </div>

          {/* Metadata sidebar */}
          <div className="w-72 shrink-0 p-5 space-y-5 border-l border-neutral-200">
            <div>
              <h4 className="text-[11px] text-neutral-400 uppercase tracking-wider font-medium mb-1.5">
                Description
              </h4>
              <p className="text-sm text-neutral-700 leading-relaxed">
                {definition?.meta.description || template.description || 'No description available'}
              </p>
            </div>

            {catalogEntry && catalogEntry.tags.length > 0 && (
              <div>
                <h4 className="text-[11px] text-neutral-400 uppercase tracking-wider font-medium mb-1.5">
                  Tags
                </h4>
                <div className="flex flex-wrap gap-1">
                  {catalogEntry.tags.map((tag: string) => (
                    <Badge key={tag} variant="default" size="sm">{getTagLabel(tag)}</Badge>
                  ))}
                </div>
              </div>
            )}

            {(() => {
              const SummaryComp = definition?.Summary;
              const samples = definition?.sampleSummaryData;
              if (!SummaryComp || !samples) return null;
              return (
                <div>
                  <h4 className="text-[11px] text-neutral-400 uppercase tracking-wider font-medium mb-1.5">
                    Summary Cell
                  </h4>
                  <div className="flex items-center gap-2 p-3 bg-neutral-50 rounded-lg border border-neutral-100">
                    {samples.map((sample: unknown, i: number) => (
                      <SummaryComp key={i} data={sample} config={{}} />
                    ))}
                  </div>
                </div>
              );
            })()}

            <div>
              <h4 className="text-[11px] text-neutral-400 uppercase tracking-wider font-medium mb-1.5">
                Last Updated
              </h4>
              <p className="text-sm text-neutral-600">
                {formatRelativeDate(template.updatedAt)}
              </p>
            </div>

            {onExport && (
              <div className="pt-2 border-t border-neutral-200">
                <button
                  onClick={() => onExport(template.id)}
                  disabled={exportingId === template.id}
                  className="w-full px-3 py-2 text-sm font-medium text-neutral-900 bg-neutral-100 border border-neutral-200 rounded-lg hover:bg-neutral-50 transition-colors disabled:opacity-50"
                >
                  {exportingId === template.id ? 'Exporting...' : 'Export Widget'}
                </button>
              </div>
            )}

          </div>
        </div>
      </ModalBody>

    </Modal>
  );
};

export default WidgetPreviewModal;
